import { useState } from "react";
import { Search, Filter, Calendar, Tag, Folder, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface FilterOptions {
  sourceLanguage?: string;
  targetLanguage?: string;
  category?: string;
  tags?: string[];
  onlyFavorites?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
}

interface HistoryFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  availableLanguages: string[];
  availableCategories: string[];
  availableTags: string[];
}

export function HistoryFilters({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  availableLanguages,
  availableCategories,
  availableTags,
}: HistoryFiltersProps) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);

  const formatLanguage = (code: string) => {
    const languages: Record<string, string> = {
      auto: "Auto-detect",
      en: "English",
      pt: "Portuguese",
      es: "Spanish",
      fr: "French",
      de: "German",
      it: "Italian",
      ja: "Japanese",
      ko: "Korean",
      zh: "Chinese",
    };
    return languages[code] || code.toUpperCase();
  };

  const handleTagToggle = (tag: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];

    onFiltersChange({
      ...filters,
      tags: newTags.length > 0 ? newTags : undefined
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    onSearchChange("");
  };

  const hasActiveFilters = Boolean(
    filters.sourceLanguage ||
    filters.targetLanguage ||
    filters.category ||
    filters.tags?.length ||
    filters.onlyFavorites ||
    filters.dateFrom ||
    filters.dateTo ||
    searchQuery
  );

  return (
    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-muted/50 rounded-lg border border-translator-border">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search translations..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 pr-12 touch-target"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={filters.onlyFavorites ? "default" : "outline"}
          size="sm"
          onClick={() => onFiltersChange({
            ...filters,
            onlyFavorites: !filters.onlyFavorites || undefined
          })}
          className="text-xs touch-target"
        >
          ⭐ Favorites Only
        </Button>

        <Button
          variant={showAdvancedFilters ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="text-xs"
        >
          <Filter className="h-3 w-3 mr-1" />
          Advanced Filters
        </Button>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-xs text-muted-foreground hover:text-destructive"
          >
            <X className="h-3 w-3 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-translator-border">
          {/* Language Filters */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Source Language</label>
            <Select
              value={filters.sourceLanguage || "all"}
              onValueChange={(value) => onFiltersChange({
                ...filters,
                sourceLanguage: value === "all" ? undefined : value
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Any language" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                <SelectItem value="all">Any language</SelectItem>
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {formatLanguage(lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Target Language</label>
            <Select
              value={filters.targetLanguage || "all"}
              onValueChange={(value) => onFiltersChange({
                ...filters,
                targetLanguage: value === "all" ? undefined : value
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Any language" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                <SelectItem value="all">Any language</SelectItem>
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {formatLanguage(lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              <Folder className="h-3 w-3 inline mr-1" />
              Category
            </label>
            <Select
              value={filters.category || "all"}
              onValueChange={(value) => onFiltersChange({
                ...filters,
                category: value === "all" ? undefined : value
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Any category" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                <SelectItem value="all">Any category</SelectItem>
                {availableCategories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              <Calendar className="h-3 w-3 inline mr-1" />
              From Date
            </label>
            <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  {filters.dateFrom ? format(filters.dateFrom, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-50" align="start">
                <CalendarComponent
                  mode="single"
                  selected={filters.dateFrom}
                  onSelect={(date) => {
                    onFiltersChange({ ...filters, dateFrom: date });
                    setDateFromOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">To Date</label>
            <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  {filters.dateTo ? format(filters.dateTo, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-50" align="start">
                <CalendarComponent
                  mode="single"
                  selected={filters.dateTo}
                  onSelect={(date) => {
                    onFiltersChange({ ...filters, dateTo: date });
                    setDateToOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Tags */}
          <div className="space-y-2 md:col-span-2 lg:col-span-1">
            <label className="text-sm font-medium">
              <Tag className="h-3 w-3 inline mr-1" />
              Tags
            </label>
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
              {availableTags.length > 0 ? (
                availableTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={filters.tags?.includes(tag) ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => handleTagToggle(tag)}
                  >
                    {tag}
                  </Badge>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">No tags available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-1 pt-2 border-t border-translator-border">
          {filters.sourceLanguage && (
            <Badge variant="secondary" className="text-xs">
              From: {formatLanguage(filters.sourceLanguage)}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onFiltersChange({ ...filters, sourceLanguage: undefined })}
                className="ml-1 h-3 w-3 p-0"
              >
                <X className="h-2 w-2" />
              </Button>
            </Badge>
          )}
          {filters.targetLanguage && (
            <Badge variant="secondary" className="text-xs">
              To: {formatLanguage(filters.targetLanguage)}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onFiltersChange({ ...filters, targetLanguage: undefined })}
                className="ml-1 h-3 w-3 p-0"
              >
                <X className="h-2 w-2" />
              </Button>
            </Badge>
          )}
          {filters.category && (
            <Badge variant="secondary" className="text-xs">
              Category: {filters.category}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onFiltersChange({ ...filters, category: undefined })}
                className="ml-1 h-3 w-3 p-0"
              >
                <X className="h-2 w-2" />
              </Button>
            </Badge>
          )}
          {filters.tags?.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              #{tag}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTagToggle(tag)}
                className="ml-1 h-3 w-3 p-0"
              >
                <X className="h-2 w-2" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}